// config/app.config.js
export const APP_CONFIG = {
  // Application Information
  APP_NAME: 'Contract Management System',
  APP_VERSION: '1.0.0',
  APP_DESCRIPTION: 'Sistem de gestionare contracte',

  // Environment
  ENVIRONMENT: process.env.NODE_ENV || 'development',
  IS_DEVELOPMENT: process.env.NODE_ENV === 'development',
  IS_PRODUCTION: process.env.NODE_ENV === 'production',

  // Features
  FEATURES: {
    ENABLE_DEBUG: process.env.REACT_APP_ENABLE_DEBUG === 'true',
    ENABLE_ANALYTICS: process.env.REACT_APP_ENABLE_ANALYTICS === 'true',
    ENABLE_NOTIFICATIONS: true,
    ENABLE_DARK_MODE: true,
    ENABLE_BULK_ACTIONS: true,
    ENABLE_EXCEL_IMPORT: true,
    ENABLE_PDF_PREVIEW: true
  },

  // UI Settings
  UI: {
    THEME: {
      PRIMARY_COLOR: '#007BFF',
      SECONDARY_COLOR: '#FF6600',
      SUCCESS_COLOR: '#28a745',
      DANGER_COLOR: '#dc3545',
      WARNING_COLOR: '#ffc107',
      INFO_COLOR: '#17a2b8'
    },
    LAYOUT: {
      SIDEBAR_WIDTH: '250px',
      HEADER_HEIGHT: '64px',
      MOBILE_BREAKPOINT: '768px'
    },
    ANIMATION: {
      DURATION_FAST: 150,
      DURATION_NORMAL: 300,
      DURATION_SLOW: 500
    }
  },

  // Pagination
  PAGINATION: {
    DEFAULT_PAGE_SIZE: 10,
    PAGE_SIZE_OPTIONS: [10, 25, 50, 100],
    MAX_PAGE_SIZE: 100
  },

  // File Upload
  FILE_UPLOAD: {
    MAX_SIZE_MB: 10,
    ALLOWED_EXTENSIONS: {
      EXCEL: ['.xlsx', '.xls'],
      PDF: ['.pdf'],
      DOCUMENTS: ['.pdf', '.doc', '.docx'],
      ALL: ['.pdf', '.doc', '.docx', '.xlsx', '.xls']
    },
    CHUNK_SIZE: 1024 * 1024 // 1MB chunks
  },

  // Date/Time
  DATE_TIME: {
    DEFAULT_FORMAT: 'DD/MM/YYYY',
    DATE_TIME_FORMAT: 'DD/MM/YYYY HH:mm',
    TIME_FORMAT: 'HH:mm',
    API_FORMAT: 'YYYY-MM-DDTHH:mm:ss.SSSZ',
    LOCALE: 'ro-RO',
    TIMEZONE: 'Europe/Bucharest'
  },

  // Currency
  CURRENCY: {
    DEFAULT: 'RON',
    SUPPORTED: ['RON', 'EUR', 'USD'],
    FORMAT: {
      RON: { symbol: 'lei', decimals: 2 },
      EUR: { symbol: '€', decimals: 2 },
      USD: { symbol: '$', decimals: 2 }
    }
  },

  // Validation
  VALIDATION: {
    PASSWORD_MIN_LENGTH: 6,
    FISCAL_CODE_PATTERN: /^[A-Z]{2}[0-9]{2,8}$|^[0-9]{2,10}$/,
    IBAN_PATTERN: /^[A-Z]{2}[0-9]{2}[A-Z0-9]{4}[0-9]{7}([A-Z0-9]?){0,16}$/,
    EMAIL_PATTERN: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
    PHONE_PATTERN: /^[\+]?[0-9\s\-\(\)]{10,}$/
  },

  // Storage Keys
  STORAGE_KEYS: {
    AUTH_TOKEN: 'cms_auth_token',
    REFRESH_TOKEN: 'cms_refresh_token',
    USER_PREFERENCES: 'cms_user_preferences',
    THEME: 'cms_theme',
    LANGUAGE: 'cms_language',
    SIDEBAR_COLLAPSED: 'cms_sidebar_collapsed'
  },

  // API Settings
  API: {
    RETRY_ATTEMPTS: 3,
    RETRY_DELAY: 1000, // ms
    CACHE_DURATION: 5 * 60 * 1000, // 5 minutes
    REQUEST_TIMEOUT: 30000 // 30 seconds
  },

  // Notifications
  NOTIFICATIONS: {
    DURATION: 5000, // 5 seconds
    MAX_NOTIFICATIONS: 5,
    POSITION: 'top-right',
    AUTO_DISMISS: true
  },

  // Contract Status
  CONTRACT_STATUS: {
    DRAFT: { label: 'Ciornă', color: 'gray', icon: 'draft' },
    GENERATED: { label: 'Generat', color: 'blue', icon: 'file' },
    SENT: { label: 'Trimis', color: 'yellow', icon: 'send' },
    ACCESSED: { label: 'Accesat', color: 'orange', icon: 'eye' },
    SIGNED: { label: 'Semnat', color: 'green', icon: 'check' },
    EXPIRED: { label: 'Expirat', color: 'red', icon: 'clock' },
    TERMINATED: { label: 'Reziliat', color: 'red', icon: 'ban' }
  },

  // Cron Job Status
  CRON_JOB_STATUS: {
    SCHEDULED: { label: 'Programat', color: 'blue', icon: 'calendar' },
    RUNNING: { label: 'În execuție', color: 'yellow', icon: 'play' },
    COMPLETED: { label: 'Finalizat', color: 'green', icon: 'check' },
    FAILED: { label: 'Eșuat', color: 'red', icon: 'x' },
    CANCELLED: { label: 'Anulat', color: 'gray', icon: 'ban' }
  },

  // Cron Job Types
  CRON_JOB_TYPES: {
    CONTRACT_RENEWAL: { label: 'Reînnoire Contract', icon: 'refresh' },
    ADDITIONAL_ACT: { label: 'Act Adițional', icon: 'plus' },
    EMAIL_NOTIFICATION: { label: 'Notificare Email', icon: 'mail' }
  },

  // Table Settings
  TABLE: {
    ROW_HEIGHT: 48,
    HEADER_HEIGHT: 56,
    VIRTUAL_SCROLL_THRESHOLD: 100,
    SELECTION_CHECKBOX_WIDTH: 48,
    ACTIONS_COLUMN_WIDTH: 120
  },

  // Search
  SEARCH: {
    DEBOUNCE_DELAY: 300,
    MIN_SEARCH_LENGTH: 2,
    MAX_SEARCH_LENGTH: 100
  },

  // Routes
  ROUTES: {
    HOME: '/',
    LOGIN: '/login',
    DASHBOARD: '/dashboard',
    CLIENTS: '/clients',
    SERVICES: '/services',
    CONTRACTS: '/contracts',
    TEMPLATES: '/templates',
    CRON_JOBS: '/cronjobs',
    CONTRACT_ACCESS: '/contract-access',
    NOT_FOUND: '/404'
  },

  // Error Boundaries
  ERROR_BOUNDARY: {
    ENABLE_REPORTING: process.env.REACT_APP_ENABLE_ERROR_REPORTING === 'true',
    FALLBACK_MESSAGE: 'Ceva nu a mers bine. Reîncărcați pagina sau contactați suportul.'
  }
};